import React, { useState, useEffect } from "react";
import { ArrowLeft, AlertTriangle, CheckCircle2, BookOpen } from "lucide-react";
import KnowledgeCheckGateway from "./KnowledgeCheckGateway";
import { COURSE_DATA } from "./courseData";

export default function CourseReader({ stateId, moduleId, onBack, onNextModule, isDark }) {
  const [showQuiz, setShowQuiz] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const D = isDark;

  const userString = typeof window !== "undefined" ? localStorage.getItem("user") : null;
  const user = userString ? JSON.parse(userString) : { name: "Student", email: "student" };

  const stateData  = COURSE_DATA[stateId];
  const moduleData = stateData?.modules.find((m) => m.id === moduleId);
  const moduleIdx  = stateData?.modules.findIndex((m) => m.id === moduleId) ?? 0;

  useEffect(() => { setShowQuiz(false); }, [moduleId]);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled  = window.scrollY;
      const total     = document.documentElement.scrollHeight - window.innerHeight;
      const pct       = total > 0 ? Math.min(100, Math.round((scrolled / total) * 100)) : 0;
      setReadProgress(pct);
      if (pct >= 85) setShowQuiz(true);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!moduleData) return null;

  const bg       = D ? "#0d1f3c" : "#f4f7fb";
  const panel    = D ? "#142a4d" : "#ffffff";
  const border   = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text     = D ? "#f0f4f8" : "#0d1f3c";
  const textMid  = D ? "rgba(240,244,248,0.82)" : "#334155";
  const textSub  = D ? "rgba(240,244,248,0.55)" : "#64748b";
  const inputBg  = D ? "rgba(255,255,255,0.06)" : "#f8fafc";
  const gold     = "#c9971c";
  const goldL    = "#fbbf24";

  return (
    <div style={{ background: bg, minHeight: "60vh", position: "relative", transition: "background .3s" }}>

      {/* Watermark */}
      <div style={{ pointerEvents: "none", position: "fixed", inset: 0, zIndex: 40, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", opacity: D ? 0.04 : 0.025 }}>
        <div style={{ transform: "rotate(-35deg)", whiteSpace: "nowrap", textAlign: "center", fontSize: 18, fontWeight: 900, color: D ? "#fff" : "#0d1f3c" }}>
          {Array(20).fill(`${user.email} • PilotLink USA • `).map((t, i) => (
            <p key={i} style={{ marginBottom: 40 }}>{t.repeat(5)}</p>
          ))}
        </div>
      </div>

      {/* Sticky top bar */}
      <div style={{ position: "sticky", top: 62, zIndex: 30, background: D ? "rgba(13,31,60,0.92)" : "rgba(255,255,255,0.92)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${border}`, padding: "clamp(10px,1.5vw,14px) clamp(14px,4vw,28px)" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 7, background: "none", border: "none", cursor: "pointer", color: textSub, fontSize: 13, fontWeight: 700, fontFamily: "inherit", padding: 0, flexShrink: 0 }}>
            <ArrowLeft size={15} /> <span className="hidden sm:inline">{stateData.title}</span>
          </button>
          <div style={{ fontSize: 12, fontWeight: 600, color: textSub, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "clamp(120px,40vw,360px)" }}>
            Module {moduleIdx + 1} · {moduleData.title.split(":").pop().trim()}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: gold, background: "rgba(201,151,28,0.12)", border: "1px solid rgba(201,151,28,0.25)", borderRadius: 99, padding: "3px 10px", flexShrink: 0 }}>
            {readProgress < 100 ? `${readProgress}% read` : "✓ Complete"}
          </div>
        </div>
      </div>

      {/* Reader content */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "clamp(24px,4vw,48px) clamp(16px,4vw,28px) clamp(60px,8vw,100px)", userSelect: "none", WebkitUserSelect: "none" }}>

        {/* Module title */}
        <h1 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px,4vw,36px)", fontWeight: 900, color: text, margin: "0 0 clamp(20px,3vw,32px)", lineHeight: 1.15, letterSpacing: "-.02em" }}>
          {moduleData.title}
        </h1>

        {/* Content blocks */}
        <div style={{ display: "flex", flexDirection: "column", gap: "clamp(14px,2vw,22px)" }}>
          {moduleData.content?.map((block, i) => {
            if (block.type === "paragraph") return (
              <p key={i} style={{ fontSize: "clamp(15px,2vw,18px)", color: textMid, lineHeight: 1.85, margin: 0 }}>{block.text}</p>
            );

            if (block.type === "heading") return (
              <h2 key={i} style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(17px,2.5vw,24px)", fontWeight: 800, color: text, margin: "clamp(8px,2vw,16px) 0 0", lineHeight: 1.25 }}>{block.text}</h2>
            );

            if (block.type === "list") return (
              <ul key={i} style={{ margin: 0, paddingLeft: "clamp(18px,3vw,28px)", display: "flex", flexDirection: "column", gap: 8 }}>
                {block.items.map((item, j) => (
                  <li key={j} style={{ fontSize: "clamp(14px,2vw,17px)", color: textMid, lineHeight: 1.75 }}>{item}</li>
                ))}
              </ul>
            );

            if (block.type === "warning") return (
              <div key={i} style={{ background: D ? "rgba(220,38,38,0.10)" : "#fff1f2", border: `1.5px solid ${D ? "rgba(220,38,38,0.30)" : "#fecaca"}`, borderRadius: 16, padding: "clamp(16px,2vw,22px)", display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <AlertTriangle size={18} style={{ color: D ? "#f87171" : "#dc2626", flexShrink: 0 }} />
                  <span style={{ fontSize: "clamp(13px,1.8vw,16px)", fontWeight: 800, color: D ? "#fca5a5" : "#991b1b" }}>{block.title}</span>
                </div>
                <p style={{ fontSize: "clamp(13px,1.8vw,16px)", color: D ? "rgba(252,165,165,0.85)" : "#7f1d1d", lineHeight: 1.75, margin: 0 }}>{block.text}</p>
              </div>
            );

            return null;
          })}
        </div>

        {/* Scroll-to-unlock prompt */}
        {!showQuiz && (
          <div style={{ marginTop: "clamp(32px,5vw,56px)", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "clamp(20px,3vw,32px)", borderRadius: 16, border: `2px dashed ${border}`, background: inputBg }}>
            <BookOpen size={28} style={{ color: textSub }} />
            <div style={{ fontSize: "clamp(13px,1.8vw,16px)", fontWeight: 700, color: textSub, textAlign: "center" }}>
              ↓ Scroll to the bottom to unlock the Knowledge Check
            </div>
            <div style={{ fontSize: 12, color: textSub, opacity: 0.7 }}>Read the full module first</div>
          </div>
        )}

        {/* Knowledge check gateway */}
        {showQuiz && moduleData.quiz && (
          <div style={{ marginTop: "clamp(32px,5vw,56px)", paddingTop: "clamp(24px,4vw,40px)", borderTop: `1px solid ${border}` }}>
            <KnowledgeCheckGateway
              question={moduleData.quiz.question}
              options={moduleData.quiz.options}
              onNext={onNextModule}
              isDark={D}
            />
          </div>
        )}
      </div>
    </div>
  );
}
